import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaSearch, FaTrash } from "react-icons/fa";
import { useState, useRef } from "react";
import { PlaySounds } from "../../../functions/playSounds";
import buildLink from "../../../urls";
import { axiosServer } from "../../../axiosServer";
import MessageAlert from "../../../components/messageAlert";
import PointsLoader from "../../../components/PointsLoader.jsx";
import Loader from "../../../components/loader.jsx";

const AssignToPallet = () => {
  const productIdRef = useRef(null);
  const destinationRef = useRef(null);

  const [scannedProduct, setScannedProduct] = useState(null);
  const [currentBin, setCurrentBin] = useState("");

  const [showMessgSucc, setShowMessageSucc] = useState(false);
  const [showMessgErr, setShowMessageErr] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const { playErrorSound, playSuccessSound } = PlaySounds();
  const history = useNavigate();

  const getUserIdFromLocalStorage = () => {
    return localStorage.getItem("userID");
  };

  const handleProductIdTrashClick = () => {
    productIdRef.current.value = "";
    setScannedProduct(null);
    productIdRef.current.focus();
  };

  const handleDestinationTrashClick = () => {
    destinationRef.current.value = "";
    destinationRef.current.focus();
  };

  const getProductPallet = async (e) => {
    e.preventDefault();

    const productId = productIdRef.current.value;
    if (!productId) {
      setMessage("Please enter a Product ID or SKU.");
      setShowMessageErr(true);
      setTimeout(() => {
        setShowMessageErr(false);
      }, 3000);
      playErrorSound();
      return;
    }
    setLoading(true);

    try {
      const url = buildLink("getProductPallet") + `&label=${productId}`;

      const response = await axiosServer.get(url);

      if (response.data.success) {
        // Extract pallet ID from message (e.g., "The Product should be in pallet : p1")
        // const palletMatch = response.data.message.match(/pallet\s*:\s*(\w+)/i);
        const expectedPallet = response.data?.data?.pallet

        // Create a product object with the available information
        const productData = {
          id: productId,
          label: productId,
          name: `Product ${productId}`,
          expected_pallet: expectedPallet
        };

        setScannedProduct(productData);
        setCurrentBin(expectedPallet);
        setMessage(response.data.message);
        setShowMessageSucc(true);
        setTimeout(() => {
          setShowMessageSucc(false);
        }, 3000);
        playSuccessSound();
      } else {
        throw new Error(response.data.message || "Product not found");
      }
    } catch (error) {
      playErrorSound();
      setMessage(error.response?.data?.message || error.message || "Product not found. Please enter a valid ID.");
      setShowMessageErr(true);
      setTimeout(() => {
        setShowMessageErr(false);
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  const assignProductToPallet = async (e) => {
    e.preventDefault();
    const destination = destinationRef.current.value;
    const productId = productIdRef.current.value;
    if (!destination) {
      setMessage("Please enter a pallet ID.");
      setShowMessageErr(true);
      setTimeout(() => {
        setShowMessageErr(false);
      }, 3000);
      playErrorSound();
      return;
    }
    setLoading(true);

    try {
      const url = buildLink("assignProductToPallet");

      const requestData = {
        pallet_id: destination,
        barcode: productId,
        user_id: getUserIdFromLocalStorage(),
      };

      const response = await axiosServer.post(url, requestData);

      if (response.data.success) {
        setMessage(response.data.message || "Product assigned to pallet successfully!");
        setShowMessageSucc(true);
        playSuccessSound();
        setTimeout(() => {
          setShowMessageSucc(false);
        }, 2000);
        setTimeout(() => {
          setScannedProduct(null);
          setCurrentBin("");
          productIdRef.current.value = "";
          if (destinationRef.current) destinationRef.current.value = "";
          productIdRef.current.focus();
        }, 2200);
      } else {
        throw new Error(response.data.message || "Failed to assign product to pallet");
      }
    } catch (error) {
      playErrorSound();
      setMessage(error.response?.data?.message || error.message || "Error occurred while assigning to pallet.");
      setShowMessageErr(true);
      setTimeout(() => {
        setShowMessageErr(false);
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="mb-5 sticky top-0 flex flex-col bg-white z-50">
        <div className="w-full bg-dbase h-16">
          <div className="container px-3 relative flex justify-between w-full text-white h-full">
            <div className="flex gap-7 flex-row justify-start">
              <button onClick={() => history(-1)}>
                <FaArrowLeft />
              </button>
              <h2 className="text-xl my-auto">Assign To Pallet</h2>
            </div>
          </div>
        </div>
      </div>
      <div className="container mx-auto px-4">
        <div className="flex flex-col gap-4">
          <form
            onSubmit={(e) => e.preventDefault()}
            className="flex flex-row gap-4 items-end"
          >
            <div className="relative flex-1">
              <div>Product ID or SKU</div>
              <input
                required
                type="text"
                ref={productIdRef}
                disabled={scannedProduct}
                className="border border-dlabelColor disabled:bg-dplaceHolder outline-dbase w-full rounded-md py-3 pr-10 pl-2 h-12 mt-2"
                placeholder="Enter Product ID or SKU"
                onKeyUp={(e) => {
                  if (e.key === "Enter") {
                    getProductPallet(e);
                  }
                }}
              />
              <div onClick={handleProductIdTrashClick}>
                <FaTrash className="absolute top-14 transform -translate-y-1/2 right-3 cursor-pointer opacity-20" />
              </div>
            </div>
            <button className="border border-dblack bg-dbase rounded-lg text-white px-5 h-12">
              {loading ? <Loader /> : <FaSearch />}
            </button>
          </form>

          {loading && <PointsLoader />}

          {scannedProduct && (
            <div>
              <h3 className="text-lg font-semibold">
                {scannedProduct.name || scannedProduct.label}
              </h3>
              <p className="text-md">
                Current Pallet Location:{" "}
                <span className="font-bold text-dred">{currentBin || "No pallet assigned"}</span>
              </p>
              <form
                onSubmit={(e) => e.preventDefault()}
                className="flex flex-col gap-4 mt-4"
              >
                <div className="relative flex-1">
                  <input
                    required
                    type="text"
                    ref={destinationRef}
                    className="border border-dlabelColor outline-dbase w-full rounded-md py-3 pr-8 pl-2"
                    placeholder="Enter Pallet ID"
                    onKeyUp={(e) => {
                      if (e.key === "Enter") {
                        assignProductToPallet(e);
                      }
                    }}
                  />
                  <div onClick={handleDestinationTrashClick}>
                    <FaTrash className="absolute top-1/2 transform -translate-y-1/2 right-3 cursor-pointer opacity-20" />
                  </div>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
      <MessageAlert
        message={message}
        type={showMessgErr ? "err" : "succ"}
        showMessg={showMessgErr || showMessgSucc}
      />
    </>
  );
};

export default AssignToPallet;
